import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { api, globalErrorHandler } from '../../api/create-api';
import { makeCall } from '../../auth-utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { Table, Row, Col, Button } from 'reactstrap';
import { ViewUser } from './ViewUser';
import { SearchBar } from '@galop/component-library';
import { InvitedUsers } from './InvitedUsers';
import Can from '../Shared/Can';

export const UsersList = (props) => {
    const ls = require('local-storage');
    const { config, IsDirty, Search, CurrentUser, NewResource, CloseNewResource } = props;
    const { addToast, removeToast } = useToasts();

    /* Generic variables */
    const [loading, setLoading] = useState(true);
    const [fullyLoaded, setFullyLoaded] = useState(false);
    const [resources, setResources] = useState([]);
    const [filteredResources, setFilteredResources] = useState([]);
    const [filtered, setFiltered] = useState(Search || '');
    const [viewResource, setViewResource] = useState(null);
    const [resourceIsDirty, setResourceIsDirty] = useState(false);

    /* Users Specific variable */
    const [invitedUsers, setInvitedUsers] = useState([]);
    const [assets, setAssets] = useState([]);

    useEffect(() => IsDirty(resourceIsDirty), [resourceIsDirty]);

    useEffect(() => {
        getResources(false);
        getAssets();
    }, []);

    useEffect(() => {
        filterResources();
    }, [filtered, resources]);

    useEffect(() => {
        if (NewResource) {
            setViewResource(null);
        }
    }, [NewResource]);

    const filterResources = () => {
        let filteredList = [];

        if (filtered !== "") {
            filteredList = resources.filter(resource => {
                const lcFilter = filtered.toLowerCase();

                // Search by table columns
                const name = (resource.name || '').toLowerCase(); // Name
                const userName = (resource.userName || '').toLowerCase(); // Name


                return name.includes(lcFilter)
                    || userName.includes(lcFilter);
            });
        }
        else {
            filteredList = resources;
        }
        setFilteredResources(filteredList);
    };

    const getAssets = () => {
        makeCall(async (authHeader) => {
            addToast('Loading Assets', { appearance: 'info', id: 'loading-assets' });
            const result = await api(`api/assets?loadFullObject=false`, {
                headers: authHeader
            })
                .catch(error => globalErrorHandler(error, addToast));

            if (result && result.data) {
                removeToast('loading-assets');
                setAssets(result.data);
            }
        });
    };

    const getResources = (loadFullObjects) => {
        makeCall(async (authHeader) => {
            if (!loadFullObjects) {
                addToast(`Loading ${config.pluralCapitalResourceName()}`, { appearance: 'info', id: 'loading-resources' });
            }
            const result = await api(`api/${config.controllerName}?loadFullObjects=${loadFullObjects}`, {
                headers: authHeader
            })
                .catch((error) => globalErrorHandler(error, addToast));

            if (result && result.data) {
                removeToast('loading-resources');
                if (!loadFullObjects) {
                    const data = result.data.filter(resource => resource.name.trim()).map(resource => ({
                        ...resource,
                        access: (resource.permissions || []).map(access => ({ name: access.split('.')[0], access: access.split('.')[1] })).reduce((obj, item) => {
                            return {
                                ...obj,
                                [item['name']]: item.access
                            };
                        }, {}),
                        loaded: false
                    }));

                    getResources(!loadFullObjects);
                    setFilteredResources(data);
                    setResources(data);
                }
                else {
                    const data = result.data.filter(resource => resource.name.trim()).map(resource => ({
                        ...resource,
                        access: (resource.permissions || []).map(access => ({ name: access.split('.')[0], access: access.split('.')[1] })).reduce((obj, item) => {
                            return {
                                ...obj,
                                [item['name']]: item.access
                            };
                        }, {}),
                        loaded: true
                    }));


                    setResources(data);
                    setFilteredResources(data);
                    setInvitedUsers(result.data.filter(resource => !resource.name.trim()));
                    setFullyLoaded(true);
                }
            }
            setLoading(false);
        });
    };

    const updateResource = (resource) => {
        const userInList = resources.find(usr => usr.id === resource.id);
        const index = resources.indexOf(userInList);
        const updatedResources = resources;

        updatedResources[index] = resource;
        setResources([...updatedResources]);
    };

    const changeResource = (resource) => {
        if (!resourceIsDirty) {
            setViewResource((viewResource || {}).id !== resource.id ? resource : null);
        }
    };

    return (!loading &&
        <Row className="flex-fill h-75 pb-5 pb-sm-3">
            <Col md="4" className={`h-100 flex-column ${viewResource || NewResource ? 'd-none d-md-flex' : 'd-flex '}`}>
                <div className={`py-1 ${viewResource ? 'd-none d-md-block' : ''}`}>
                    <div>
                        <SearchBar value={filtered} onChange={(value) => setFiltered(value)} placeHolder={`Search ${config.pluralCapitalResourceName()}`} Reset={() => setFiltered('')} />
                        <p className="mt-2">Use this screen to view / edit {config.pluralResourceName()} in this system.</p>
                    </div>
                </div>
                {config.listHeader &&
                    <div className={`text-primary align-middle mb-1 ${viewResource || NewResource ? 'd-none d-md-block' : ''}`}>
                        <span className="h3">{NewResource ? `New ${config.capitalResourceName}` : config.listHeader}</span>
                    </div>
                }
                <div className={`flex-fill border rounded scrollable-list ${viewResource || NewResource ? 'd-none d-md-block' : ''}`} style={{ overflow: 'scroll' }}>
                    <Table hover={!resourceIsDirty} className={`mb-0 border-bottom`}>
                        <tbody>
                            {(filteredResources || []).map(resource => (
                                <tr key={resource.id || Math.random()} className={`${resourceIsDirty ? '' : 'cursor-pointer'} ${resource.id === (viewResource || {}).id ? 'bg-secondary text-white' : !resource.deleted ? '' : 'text-dark bg-off-white'}`}>
                                    <td className={`${!resource.role ? 'font-italic text-white bg-primary' : ''} p-0`}>
                                        <div className="d-flex align-items-stretch px-1">
                                            <div className="flex-fill py-3 pl-1" onClick={() => changeResource(resource)}>
                                                <span className="align-middle">
                                                    {resource.firstName || resource.lastName ? `${resource.firstName || ''} ${resource.lastName || ''}` : (resource.name || '')}
                                                    {resource.id === (viewResource || {}).id && <span className="float-right text-white pr-1"><FontAwesomeIcon icon={faTimes} /> Close</span>}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Col>
            <Col md="8" className={`${viewResource ? 'border-left h-100' : 'd-none d-md-block'}`}>
                {(viewResource || NewResource) ?
                    <ViewUser key={(viewResource || {}).id}
                        {...{
                            config,
                            resource: viewResource,
                            NewResource,
                            assets,
                            CurrentUser
                        }}
                        Save={(resource) => {
                            updateResource(resource);
                            setResourceIsDirty(false);
                        }}
                        Close={() => {
                            setViewResource(null);
                            setResourceIsDirty(false);
                            CloseNewResource();
                        }}
                        IsDirty={(isDirty) => setResourceIsDirty(isDirty)}
                        ReloadResources={() => getResources(true)}
                    />
                    :
                    (fullyLoaded && invitedUsers && invitedUsers.length > 0 && <InvitedUsers users={invitedUsers} ReloadResources={() => getResources(true)} />)
                }
            </Col>
        </Row>);
};


// WEBPACK FOOTER //
// ./src/components/Users/UsersList.js